import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import { useLocale } from '@shared/hooks/locale';

type ConfirmDialogProps = React.PropsWithChildren<{
  title: React.ReactNode;
  open: boolean;
  okDisabled?: boolean;
  className?: string;
  error?: null | { message: string; details: string[] };
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  okText?: React.ReactNode;
  cancelText?: React.ReactNode;
}>;

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  className,
  title,
  children,
  open,
  error,
  isSubmitting = false,
  onClose,
  onSubmit,
  okDisabled = false,
  okText,
  cancelText,
}) => {
  const { t } = useLocale();

  return (
    <Dialog className={className} open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>

      {error != null && (
        <DialogContent>
          <Alert severity="error" sx={{ mb: 1 }}>
            <AlertTitle>{error.message}</AlertTitle>
            <ul>
              {error.details.map((errDetails, i) => (
                <li key={`error-detail-${i}`} css={{ mb: 8 }}>
                  {errDetails}
                </li>
              ))}
            </ul>
          </Alert>
        </DialogContent>
      )}

      {children}
      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose}>
          {cancelText ?? t('Cancel')}
        </Button>
        <LoadingButton
          loading={isSubmitting}
          disabled={okDisabled}
          color="primary"
          onClick={onSubmit}
          variant="text"
          loadingIndicator={
            <CircularProgress disableShrink color="inherit" size={16} />
          }
        >
          {okText ?? t('ok')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
